/**
 * AdminMunicipal 2.0 - Mobile Design System
 * Professional, modern design tokens for native-like mobile experience
 */

export const DS = {
  /**
   * Color Palette
   * Based on brand colors with semantic naming
   */
  colors: {
    // Brand
    primary: '#5D1826',        // Burgundy (Main brand)
    primaryLight: '#7A2435',   // Lighter burgundy (Hover states)
    primaryDark: '#3a0d16',    // Darker burgundy (Gradients)
    secondary: '#B89658',      // Gold (Accents, icons)
    secondaryLight: '#D4AF37', // Lighter gold (Hover)

    // Neutrals
    bg: '#F3F4F6',            // Light gray (Background)
    surface: '#FFFFFF',        // White (Cards, modals)
    surfaceHover: '#F9FAFB',  // Very light gray (Hover)

    // Text
    textMain: '#1F2937',       // Dark gray (Primary text)
    textSecondary: '#4B5563',  // Medium dark gray
    textMuted: '#6B7280',      // Medium gray (Secondary text)
    textLight: '#9CA3AF',      // Light gray (Placeholders)

    // Borders
    border: '#E5E7EB',         // Light gray (Borders)
    borderLight: '#F3F4F6',    // Very light (Dividers)
    borderDark: '#D1D5DB',     // Medium gray (Focused borders)

    // Status
    success: '#059669',        // Green
    successLight: '#D1FAE5',   // Light green bg
    warning: '#D97706',        // Amber
    warningLight: '#FEF3C7',   // Light amber bg
    error: '#DC2626',          // Red
    errorLight: '#FEE2E2',     // Light red bg
    info: '#3B82F6',           // Blue
    infoLight: '#DBEAFE',      // Light blue bg
  },

  /**
   * Typography Scale
   * Consistent text styles across the app
   */
  typography: {
    // Headers
    h1: 'text-2xl font-bold text-gray-900 leading-tight',
    h2: 'text-xl font-bold text-gray-800 leading-snug',
    h3: 'text-lg font-bold text-gray-900 leading-snug',
    h4: 'text-base font-bold text-gray-900',
    h5: 'text-sm font-bold text-gray-900',

    // Body
    bodyLarge: 'text-base text-gray-700',
    body: 'text-sm text-gray-600',
    bodySmall: 'text-xs text-gray-500',

    // UI Elements
    label: 'text-xs font-bold text-gray-400 uppercase tracking-wider',
    labelDark: 'text-xs font-bold text-gray-600 uppercase tracking-wide',
    button: 'text-sm font-medium',
    buttonLarge: 'text-base font-semibold',
    badge: 'text-[10px] font-bold uppercase tracking-wide',
    caption: 'text-[10px] text-gray-400',
  },

  /**
   * Spacing System
   * Consistent spacing for layout
   */
  spacing: {
    // Padding/Margin presets
    section: 'px-5 py-6',
    sectionCompact: 'px-5 py-4',
    card: 'p-5',
    cardCompact: 'p-4',
    header: 'px-4 py-3',
    bottomNav: 'pt-2 px-2 pb-safe',

    // Gaps
    gap: {
      tight: 'gap-1',
      close: 'gap-1.5',
      normal: 'gap-3',
      comfortable: 'gap-4',
      loose: 'gap-6',
    },

    // Specific measurements
    touchTarget: 'min-h-[44px]',
    avatarSm: 'w-6 h-6',
    avatarMd: 'w-8 h-8',
    avatarLg: 'w-12 h-12',
    iconSm: 'w-4 h-4',
    iconMd: 'w-5 h-5',
    iconLg: 'w-6 h-6',
  },

  /**
   * Shadow System
   * Modern, subtle shadows
   */
  shadows: {
    // Cards
    sm: 'shadow-[0_2px_8px_rgba(0,0,0,0.04)] ring-1 ring-gray-100',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-2xl',

    // With color tints
    primaryLight: 'shadow-lg shadow-red-900/20',
    secondaryLight: 'shadow-lg shadow-yellow-600/20',

    // Navigation
    nav: 'shadow-[0_-5px_15px_rgba(0,0,0,0.03)]',
    header: 'shadow-sm',

    // None
    none: 'shadow-none',
  },

  /**
   * Border Radius
   * Consistent rounding
   */
  radius: {
    none: 'rounded-none',
    sm: 'rounded-lg',       // 8px
    md: 'rounded-xl',       // 12px
    lg: 'rounded-2xl',      // 16px
    xl: 'rounded-3xl',      // 24px
    full: 'rounded-full',
  },

  /**
   * Animation Presets
   * Smooth, native-like animations
   *
   * Standard easing curve: [0.4, 0, 0.2, 1] (Material Design decelerate)
   * - Use for most UI animations
   * - Provides smooth, natural-feeling motion
   */
  animations: {
    // Duration (in seconds for Framer Motion)
    duration: {
      instant: 0,
      fast: 0.1,      // Micro-interactions (button taps)
      normal: 0.2,    // Most UI transitions
      slow: 0.3,      // Emphasis animations
      slower: 0.4,    // Page transitions
    },

    // Easing curves
    easing: {
      // Standard curve - use for most animations
      default: [0.4, 0, 0.2, 1] as [number, number, number, number],
      // Enter screen - elements entering view
      enter: [0, 0, 0.2, 1] as [number, number, number, number],
      // Exit screen - elements leaving view
      exit: [0.4, 0, 1, 1] as [number, number, number, number],
      // Sharp - for quick interactions
      sharp: [0.4, 0, 0.6, 1] as [number, number, number, number],
      // CSS-compatible strings
      css: {
        default: 'cubic-bezier(0.4, 0, 0.2, 1)',
        enter: 'cubic-bezier(0, 0, 0.2, 1)',
        exit: 'cubic-bezier(0.4, 0, 1, 1)',
      }
    },

    // ============================================
    // Framer Motion Presets (spreadable props)
    // Usage: <motion.div {...DS.animations.fadeIn}>
    // ============================================

    // Fade in with subtle upward motion
    fadeIn: {
      initial: { opacity: 0, y: 10 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: 10 },
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Slide in from left
    slideIn: {
      initial: { opacity: 0, x: -20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: 20 },
      transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Modal/dialog animation
    modal: {
      initial: { opacity: 0, scale: 0.95, y: 20 },
      animate: { opacity: 1, scale: 1, y: 0 },
      exit: { opacity: 0, scale: 0.95, y: 20 },
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Modal for mobile (no scale, slide up)
    modalMobile: {
      initial: { opacity: 0, y: '100%' },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: '100%' },
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Backdrop/overlay
    backdrop: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Dropdown menu animation
    dropdown: {
      initial: { opacity: 0, y: 8, scale: 0.95 },
      animate: { opacity: 1, y: 0, scale: 1 },
      exit: { opacity: 0, y: 8, scale: 0.95 },
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Tab content transition
    tabContent: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Error/alert message
    errorMessage: {
      initial: { opacity: 0, height: 0, marginBottom: 0 },
      animate: { opacity: 1, height: 'auto', marginBottom: 16 },
      exit: { opacity: 0, height: 0, marginBottom: 0 },
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // List container (stagger children)
    listContainer: {
      initial: { opacity: 0 },
      animate: {
        opacity: 1,
        transition: { staggerChildren: 0.05 }
      },
      exit: { opacity: 0 }
    },

    // List item (for staggered animations)
    listItem: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -10 },
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // ============================================
    // Interaction Presets (for whileHover/whileTap)
    // Usage: <motion.button whileTap={DS.animations.buttonTap}>
    // ============================================

    // Card hover effect
    cardHover: {
      y: -2,
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Card tap effect
    cardTap: {
      scale: 0.98,
      transition: { duration: 0.1 }
    },

    // Button tap effect
    buttonTap: {
      scale: 0.95,
      transition: { duration: 0.1 }
    },

    // Subtle tap for larger touch targets
    subtleTap: {
      scale: 0.98,
      transition: { duration: 0.1 }
    },

    // Nav item active state
    navItemActive: {
      y: -4,
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // Nav item inactive state
    navItemInactive: {
      y: 0,
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    // ============================================
    // Sidebar animations
    // ============================================
    sidebar: {
      transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },

    sidebarContent: {
      initial: { opacity: 0, x: -10 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -10 },
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
    },
  },

  /**
   * Layout
   * Common layout patterns
   */
  layout: {
    container: 'max-w-md mx-auto',
    fullHeight: 'min-h-screen',
    flexCenter: 'flex items-center justify-center',
    flexBetween: 'flex items-center justify-between',
    flexCol: 'flex flex-col',
    grid2: 'grid grid-cols-2 gap-4',
    grid3: 'grid grid-cols-3 gap-4',
  },

  /**
   * Z-Index Scale
   * Proper layering
   */
  zIndex: {
    base: 'z-0',
    dropdown: 'z-10',
    sticky: 'z-20',
    modal: 'z-50',
    nav: 'z-50',
    modalBackdrop: 'z-40',
    toast: 'z-60',
  },

  /**
   * Transitions
   * CSS transition classes
   */
  transitions: {
    all: 'transition-all',
    colors: 'transition-colors',
    transform: 'transition-transform',
    opacity: 'transition-opacity',
    shadow: 'transition-shadow',
    fast: 'duration-100',
    normal: 'duration-200',
    slow: 'duration-300',
  },
};

/**
 * Helper function to combine DS classes
 */
export const cn = (...classes: (string | undefined | null | false)[]) => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Status color helper
 */
export const getStatusColor = (status: string) => {
  const statusMap: Record<string, string> = {
    'Confirmada': 'bg-green-100 text-green-700',
    'Aprobado': 'bg-green-100 text-green-700',
    'Completada': 'bg-green-100 text-green-700',
    'Pendiente': 'bg-amber-100 text-amber-700',
    'En Revisión': 'bg-amber-100 text-amber-700',
    'Registrada': 'bg-blue-100 text-blue-700',
    'Finalizada': 'bg-gray-100 text-gray-600',
    'Rechazado': 'bg-red-100 text-red-700',
    'Cancelada': 'bg-red-100 text-red-700',
  };

  return statusMap[status] || 'bg-gray-100 text-gray-600';
};

/**
 * Icon size helper
 */
export const getIconSize = (size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizes = {
    sm: 14,
    md: 16,
    lg: 20,
  };
  return sizes[size];
};

export default DS;
